import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Shield, Search, MoreVertical, HelpCircle, FileText, HelpingHand, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf39cd38-3e4c-4cdf-8fd4-859e0591d26f/artifacts/0ftkiwrk_IMG_2860.png";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);
  const moreMenuRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (path) => location.pathname === path;

  // Handle scroll for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside search and more menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced API search
  const performSearch = useCallback(async (query) => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API_URL}/api/search`, {
        params: { q: query }
      });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input with debounce
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchResultClick = (result) => {
    setSearchQuery("");
    setShowSearchResults(false);
    navigate(result.url, { state: { searchQuery: result.title } });
  };

  // Page links for navigation
  const pageLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/faq", label: "FAQ" },
    { path: "/blog", label: "Blog" },
    { path: "/privacy", label: "Privacy" },
    { path: "/contact", label: "Contact" },
  ];

  const scrollToHowItWorks = () => {
    if (location.pathname === '/') {
      const element = document.getElementById('how-it-works');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById('how-it-works');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className={`sticky top-0 z-50 bg-white transition-shadow duration-200 ${scrolled ? 'shadow-md' : 'shadow-sm'}`}>
      {/* Main Navigation Row */}
      <div className="border-b border-gray-100">
        <div className="container-main">
          <div className="flex items-center h-[72px] md:h-[80px] gap-4">
            
            {/* Logo - Always visible on all screen sizes */}
            <Link 
              to="/" 
              className="flex items-center flex-shrink-0"
              data-testid="logo-link"
            >
              <picture>
                <source srcSet={LOGO_URL} type="image/png" />
                <img 
                  src={LOGO_URL} 
                  alt="Financify" 
                  className="h-14 md:h-16 w-auto"
                  loading="eager"
                  onError={(e) => {
                    // Replace with text fallback if image fails to load
                    e.target.outerHTML = '<span class="text-xl md:text-2xl font-bold text-[#2D3748]">finan<span style="color: #52BFB8">cify</span></span>';
                  }}
                />
              </picture>
            </Link>

            {/* Mobile Brand Text - fills empty space next to logo */}
            <div className="flex md:hidden flex-1 items-center justify-center">
              <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: '#4A4A4A' }}>
                finan<span style={{ color: '#FDD552' }}>cify</span>
              </span>
            </div>

            {/* Wide Search Bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-xl" ref={searchRef}>
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  {isSearching ? (
                    <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                  ) : (
                    <Search className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Search Financify..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchQuery.length > 1 && performSearch(searchQuery)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-100 border-0 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#52BFB8]/30 focus:bg-white transition-all"
                  data-testid="search-input"
                />
                
                {/* Search Results Dropdown */}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                    <div className="p-2 max-h-80 overflow-y-auto">
                      {/* Group FAQ results */}
                      {searchResults.some(r => r.type === "faq") && (
                        <>
                          <p className="text-xs text-gray-400 px-3 py-1 flex items-center gap-1">
                            <HelpingHand className="w-3 h-3" /> FAQ
                          </p>
                          {searchResults.filter(r => r.type === "faq").map((result, index) => (
                            <button
                              key={`faq-${index}`}
                              onClick={() => handleSearchResultClick(result)}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <p className="text-sm font-medium text-gray-800 truncate">{result.title}</p>
                              <p className="text-xs text-gray-500 truncate">{result.excerpt}</p>
                            </button>
                          ))}
                        </>
                      )}
                      
                      {/* Group Blog results */}
                      {searchResults.some(r => r.type === "blog") && (
                        <>
                          <p className="text-xs text-gray-400 px-3 py-1 mt-2 flex items-center gap-1 border-t border-gray-100 pt-2">
                            <FileText className="w-3 h-3" /> Blog Articles
                          </p>
                          {searchResults.filter(r => r.type === "blog").map((result, index) => (
                            <button
                              key={`blog-${index}`}
                              onClick={() => handleSearchResultClick(result)}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <p className="text-sm font-medium text-gray-800 truncate">{result.title}</p>
                              <p className="text-xs text-gray-500 truncate">{result.excerpt}</p>
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                    <div className="flex border-t border-gray-100">
                      <Link 
                        to="/faq" 
                        onClick={() => setShowSearchResults(false)}
                        className="flex-1 px-4 py-2 text-sm text-center text-[#52BFB8] hover:bg-gray-50"
                      >
                        All FAQ →
                      </Link>
                      <Link 
                        to="/blog" 
                        onClick={() => setShowSearchResults(false)}
                        className="flex-1 px-4 py-2 text-sm text-center text-[#52BFB8] hover:bg-gray-50 border-l border-gray-100"
                      >
                        All Blog →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Section - How it works, Auth, Menu */}
            <div className="flex items-center gap-1 md:gap-3 ml-auto">
              {/* How it Works Link */}
              <button
                onClick={scrollToHowItWorks}
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#52BFB8] hover:text-[#3DA8A1] transition-colors"
                data-testid="how-it-works-link"
              >
                <HelpCircle className="w-4 h-4" />
                How it works
              </button>

              {/* Divider */}
              <div className="hidden md:block w-px h-6 bg-gray-200"></div>

              {/* Auth Section */}
              {user ? (
                <>
                  <Link to="/dashboard" className="hidden md:inline-flex" data-testid="dashboard-link">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-gray-700 font-medium hover:bg-gray-100"
                    >
                      Dashboard
                    </Button>
                  </Link>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={logout}
                    className="hidden md:inline-flex text-gray-400 hover:text-gray-600"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" className="hidden md:inline-flex" data-testid="login-link">
                    <span className="text-sm font-medium text-gray-700 hover:text-gray-900 px-2 py-1">
                      Log In
                    </span>
                  </Link>
                  <Link to="/signup" data-testid="signup-link" className="hidden md:inline-flex">
                    <Button 
                      size="sm"
                      className="bg-[#52BFB8] hover:bg-[#3DA8A1] text-white font-medium rounded-full px-5"
                    >
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}

              {/* More Menu (3 dots) - Desktop */}
              <div className="hidden md:block relative" ref={moreMenuRef}>
                <button
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  data-testid="more-menu-btn"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                
                {showMoreMenu && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                    <Link
                      to="/admin"
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <Shield className="w-4 h-4" />
                      Admin Panel
                    </Link>
                    <Link
                      to="/qualify"
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-[#52BFB8] hover:bg-gray-50 transition-colors border-t border-gray-100"
                    >
                      Get Started →
                    </Link>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Navigation Row - Centered Links */}
      <div className="bg-gray-50/50 hidden md:block border-b border-gray-100">
        <div className="container-main">
          <div className="flex items-center justify-center py-2">
            <nav className="flex items-center gap-1">
              {pageLinks.map((link, index) => (
                <div key={link.path} className="flex items-center">
                  <Link
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                    className={`px-4 py-1.5 text-sm font-medium transition-all duration-200 ${
                      isActive(link.path)
                        ? "text-[#52BFB8]"
                        : "text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    {link.label}
                  </Link>
                  {index < pageLinks.length - 1 && (
                    <span className="text-gray-300 mx-1">·</span>
                  )}
                </div>
              ))}
              <span className="text-gray-300 mx-1">·</span>
              <Link
                to="/partners/verify"
                data-testid="nav-partners"
                className={`px-4 py-1 text-sm font-semibold rounded-full transition-all duration-200 ${
                  isActive("/partners/verify")
                    ? "bg-[#52BFB8] text-white"
                    : "bg-[#52BFB8]/10 text-[#52BFB8] hover:bg-[#52BFB8]/20"
                }`}
              >
                Partners
              </Link>
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="container-main py-4">
            {/* Mobile Search */}
            <div className="relative mb-4" ref={searchRef}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {isSearching ? (
                  <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 text-gray-400" />
                )}
              </div>
              <input
                type="text"
                placeholder="Search Financify..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchQuery.length > 1 && performSearch(searchQuery)}
                className="w-full pl-10 pr-4 py-3 bg-gray-100 border-0 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#52BFB8]/30"
                data-testid="mobile-search-input"
              />
              
              {/* Mobile Search Results */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                  <div className="p-2 max-h-64 overflow-y-auto">
                    {/* FAQ results */}
                    {searchResults.some(r => r.type === "faq") && (
                      <>
                        <p className="text-xs text-gray-400 px-3 py-1 flex items-center gap-1">
                          <HelpingHand className="w-3 h-3" /> FAQ
                        </p>
                        {searchResults.filter(r => r.type === "faq").slice(0, 3).map((result, index) => (
                          <button
                            key={`mobile-faq-${index}`}
                            onClick={() => {
                              handleSearchResultClick(result);
                              setMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <p className="text-sm font-medium text-gray-800 truncate">{result.title}</p>
                            <p className="text-xs text-gray-500 truncate">{result.excerpt}</p>
                          </button>
                        ))}
                      </>
                    )}
                    
                    {/* Blog results */}
                    {searchResults.some(r => r.type === "blog") && (
                      <>
                        <p className="text-xs text-gray-400 px-3 py-1 mt-2 flex items-center gap-1 border-t border-gray-100 pt-2">
                          <FileText className="w-3 h-3" /> Blog
                        </p>
                        {searchResults.filter(r => r.type === "blog").slice(0, 3).map((result, index) => (
                          <button
                            key={`mobile-blog-${index}`}
                            onClick={() => {
                              handleSearchResultClick(result);
                              setMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <p className="text-sm font-medium text-gray-800 truncate">{result.title}</p>
                            <p className="text-xs text-gray-500 truncate">{result.excerpt}</p>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                  <div className="flex border-t border-gray-100">
                    <Link 
                      to="/faq" 
                      onClick={() => {
                        setShowSearchResults(false);
                        setMobileMenuOpen(false);
                      }}
                      className="flex-1 px-4 py-2 text-sm text-center text-[#52BFB8] hover:bg-gray-50"
                    >
                      All FAQ
                    </Link>
                    <Link 
                      to="/blog" 
                      onClick={() => {
                        setShowSearchResults(false);
                        setMobileMenuOpen(false);
                      }}
                      className="flex-1 px-4 py-2 text-sm text-center text-[#52BFB8] hover:bg-gray-50 border-l border-gray-100"
                    >
                      All Blog
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex flex-col gap-1">
              <button
                onClick={scrollToHowItWorks}
                className="px-4 py-3 rounded-xl font-medium text-[#52BFB8] hover:bg-[#52BFB8]/5 text-left flex items-center gap-2"
              >
                <HelpCircle className="w-4 h-4" />
                How it works?
              </button>
              
              {pageLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                    isActive(link.path)
                      ? "bg-[#52BFB8]/10 text-[#52BFB8]"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              <Link
                to="/partners/verify"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-partners"
                className={`px-4 py-3 rounded-xl font-semibold transition-colors ${
                  isActive("/partners/verify")
                    ? "bg-[#52BFB8] text-white"
                    : "bg-[#52BFB8]/10 text-[#52BFB8]"
                }`}
              >
                Partners
              </Link>
              <div className="border-t border-gray-100 my-2"></div>
              
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 rounded-xl font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 rounded-xl font-medium text-gray-500 hover:bg-gray-50 flex items-center gap-2 text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </>
              ) : (
                <div className="flex gap-2 px-4 pt-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full">
                      Log In
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                    <Button className="w-full bg-[#52BFB8] hover:bg-[#3DA8A1]">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
              
              <Link
                to="/admin"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-xl font-medium text-gray-400 hover:bg-gray-50 flex items-center gap-2"
              >
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
